import React, { useState } from "react";
import { Copy, Download, Check, BookOpen, ListChecks, Lightbulb, Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import MoonPhase from "@/components/MoonPhase";

function summaryToText(s) {
  const c = s.content;
  const lines = [
    `${c.title || s.title}`,
    `Subject: ${s.subject}`,
    "",
    "OVERVIEW",
    c.overview || "",
    "",
    "MAIN IDEAS",
    ...(c.main_ideas || []).map((m) => `- ${m}`),
    "",
    "KEY DEFINITIONS",
    ...(c.key_definitions || []).map((d) => `- ${d.term}: ${d.definition}`),
    "",
    "IMPORTANT FACTS",
    ...(c.important_facts || []).map((f) => `- ${f}`),
    "",
    "WHAT YOU MUST REMEMBER",
    c.must_remember || "",
  ];
  return lines.join("\n");
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="glass rounded-2xl p-6" data-testid={`summary-section-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="mb-4 flex items-center gap-2.5">
        <Icon className="h-4 w-4 text-zinc-300" strokeWidth={1.5} />
        <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{title}</h3>
      </div>
      {children}
    </section>
  );
}

export default function SummaryView({ summary }) {
  const c = summary.content || {};
  const [copied, setCopied] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const quiz = c.quiz || [];

  const copy = async () => {
    await navigator.clipboard.writeText(summaryToText(summary));
    setCopied(true);
    toast.success("Summary copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const download = () => {
    const blob = new Blob([summaryToText(summary)], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(c.title || summary.title || "summary").replace(/\s+/g, "_")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const submitQuiz = async () => {
    setSubmitted(true);
    const correct = quiz.reduce((acc, q, i) => acc + (answers[i] === q.correct_index ? 1 : 0), 0);
    try {
      await api.post("/quiz/attempt", {
        subject: summary.subject,
        summary_id: summary.summary_id,
        correct,
        total: quiz.length,
      });
      toast.success(`You scored ${correct}/${quiz.length}`);
    } catch {
      toast.error("Could not save your quiz result");
    }
  };

  const score = submitted
    ? quiz.reduce((acc, q, i) => acc + (answers[i] === q.correct_index ? 1 : 0), 0)
    : 0;

  return (
    <div className="space-y-6" data-testid="summary-view">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <span className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300" data-testid="summary-subject-badge">
            {summary.subject} · {summary.source}
          </span>
          <h1 className="mt-3 font-sans text-3xl font-light tracking-tight text-white md:text-4xl" data-testid="summary-title">
            {c.title || summary.title}
          </h1>
        </div>
        <div className="flex gap-2">
          <button onClick={copy} data-testid="summary-copy-button" className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-zinc-300 transition-colors hover:border-white/30 hover:text-white">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copy
          </button>
          <button onClick={download} data-testid="summary-download-button" className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-zinc-300 transition-colors hover:border-white/30 hover:text-white">
            <Download className="h-4 w-4" /> .txt
          </button>
        </div>
      </div>

      {c.overview && (
        <Section icon={BookOpen} title="Overview">
          <p className="leading-relaxed text-zinc-300">{c.overview}</p>
        </Section>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {!!(c.main_ideas || []).length && (
          <Section icon={ListChecks} title="Main Ideas">
            <ul className="space-y-2.5">
              {c.main_ideas.map((m, i) => (
                <li key={i} className="flex gap-3 text-zinc-300">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-white/50" />
                  <span className="leading-relaxed">{m}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {!!(c.important_facts || []).length && (
          <Section icon={Star} title="Important Facts">
            <ul className="space-y-2.5">
              {c.important_facts.map((f, i) => (
                <li key={i} className="flex gap-3 text-zinc-300">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-white/50" />
                  <span className="leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      {!!(c.key_definitions || []).length && (
        <Section icon={BookOpen} title="Key Definitions">
          <div className="grid gap-4 sm:grid-cols-2">
            {c.key_definitions.map((d, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <p className="font-sans text-sm font-semibold text-white">{d.term}</p>
                <p className="mt-1 text-sm leading-relaxed text-zinc-400">{d.definition}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {c.must_remember && (
        <section className="rounded-2xl border border-white/20 bg-white/[0.06] p-6" data-testid="summary-must-remember">
          <div className="mb-3 flex items-center gap-2.5">
            <Lightbulb className="h-4 w-4 text-white" strokeWidth={1.5} />
            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-white">What You Must Remember</h3>
          </div>
          <p className="leading-relaxed text-zinc-100">{c.must_remember}</p>
        </section>
      )}

      {!!quiz.length && (
        <section className="glass rounded-2xl p-6" data-testid="summary-quiz">
          <div className="mb-5 flex items-center justify-between">
            <h3 className="font-sans text-xl font-light tracking-tight text-white">Test Your Knowledge</h3>
            {submitted && (
              <div className="flex items-center gap-2">
                <MoonPhase accuracy={(score / quiz.length) * 100} size={22} />
                <span className="font-mono text-sm text-zinc-300" data-testid="quiz-score">{score}/{quiz.length}</span>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {quiz.map((q, qi) => (
              <div key={qi} data-testid={`quiz-question-${qi}`}>
                <p className="mb-3 font-medium text-zinc-100">{qi + 1}. {q.question}</p>
                <div className="grid gap-2">
                  {q.options.map((opt, oi) => {
                    const selected = answers[qi] === oi;
                    const isCorrect = oi === q.correct_index;
                    let cls = "border-white/10 text-zinc-300 hover:border-white/25";
                    if (submitted) {
                      if (isCorrect) cls = "border-white/70 bg-white/10 text-white";
                      else if (selected) cls = "border-destructive/60 bg-destructive/10 text-zinc-300";
                      else cls = "border-white/5 text-zinc-500";
                    } else if (selected) {
                      cls = "border-white/50 bg-white/5 text-white";
                    }
                    return (
                      <button
                        key={oi}
                        disabled={submitted}
                        onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                        data-testid={`quiz-${qi}-option-${oi}`}
                        className={`rounded-xl border px-4 py-2.5 text-left text-sm transition-colors ${cls}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
                {submitted && q.explanation && (
                  <p className="mt-2 text-xs leading-relaxed text-zinc-500">{q.explanation}</p>
                )}
              </div>
            ))}
          </div>

          {!submitted && (
            <button
              onClick={submitQuiz}
              disabled={Object.keys(answers).length < quiz.length}
              data-testid="quiz-submit-button"
              className="shimmer mt-6 rounded-full bg-white px-6 py-2.5 text-sm font-semibold text-[#06080d] transition-opacity disabled:opacity-40"
            >
              Submit answers
            </button>
          )}
        </section>
      )}
    </div>
  );
}
